import { omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { View } from 'react-native';
import DropDownPicker, {
  DropDownPickerProps,
  ItemType,
  ListModeType,
  ValueType,
} from 'react-native-dropdown-picker';
import { HelperText } from 'react-native-paper';
import { Colors, useThemeMode } from '../../themes';
import { SearchInput } from '../crop-circle/Components/SearchInput';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

const DropDownPickerSpecificPropsList: (keyof DropDownPickerProps<ValueType>)[] =
  [
    'placeholder',
    'open',
    'setOpen',
    'onChangeValue',
    'items',
    'value',
    'setValue',
  ];

type DropDownPickerSpecificProps =
  | 'placeholder'
  | 'open'
  | 'setOpen'
  | 'onChangeValue'
  | 'items'
  | 'value'
  | 'setValue';

type BasePropsSelect<T extends ValueType> = {
  errorMessage?: string;
  label: string;
  dropDownPickerProps?: Omit<
    DropDownPickerProps<T>,
    DropDownPickerSpecificProps
  > & { [key: string]: any };
  items: ItemType<T>[];
  listMode?: ListModeType;
};

type SelectProps<T extends ValueType> = BasePropsSelect<T> & {
  onSelectItem: (item: T | null) => void;
  listMode?: ListModeType;
  disabled?: boolean;
  dropDownContainerStyle?: any;
  searchable?: boolean;
  searchPlaceholder?: string;
};

export type ControlledSelectProps<T extends ValueType> = BasePropsSelect<T> & {
  name: string;
  errors?: FieldErrors;
  control: Control<any>;
  onSelectItem?: (item: T | null) => void;
  disabled?: boolean;
  dropDownContainerStyle?: any;
};

export const ControlledSelect = <T extends ValueType>(
  props: ControlledSelectProps<T>
) => {
  const errorMessage = props.errors?.[props.name]?.message?.toString();

  const cleanedDropDownPickerProps = props.dropDownPickerProps
    ? omit(props.dropDownPickerProps, DropDownPickerSpecificPropsList)
    : {};

  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field: { onChange, value } }) => (
        <Select
          label={props.label}
          errorMessage={errorMessage}
          items={props.items}
          listMode={props.listMode}
          disabled={props.disabled}
          dropDownContainerStyle={props.dropDownContainerStyle}
          onSelectItem={(item) => {
            if (value === item || (value == null && item == null)) return;
            onChange(item);
          }}
          dropDownPickerProps={{
            ...cleanedDropDownPickerProps,
            value: value as T,
          }}
        />
      )}
    />
  );
};

export const Select = <T extends ValueType>(props: SelectProps<T>) => {
  const {
    label,
    errorMessage,
    onSelectItem,
    dropDownPickerProps,
    dropDownContainerStyle,
    items,
    disabled,
    searchable,
    searchPlaceholder,
  } = props;

  const {
    selectedItemContainerStyle,
    selectedItemLabelStyle,
    showTickIcon = false,
    TickIconComponent,
  } = props.dropDownPickerProps || {};

  const themeMode = useThemeMode();

  const [open, setOpen] = useState(
    dropDownPickerProps?.openDropdownOnLoad || false
  );
  const [searchText, setSearchText] = useState('');
  const [internalValue, setInternalValue] = useState<T | null>(
    (dropDownPickerProps?.value as T) ?? null
  );
  // When used inside Controller, value comes from dropDownPickerProps; use it so selection updates correctly
  const controlledValue = dropDownPickerProps?.value as T | undefined | null;
  const value =
    controlledValue !== undefined && controlledValue !== null
      ? controlledValue
      : internalValue;

  // When controlled, only notify parent when value actually changed (user selection). Skip when library is just syncing to current value.
  const notifySelect = useCallback(
    (item: T | null) => {
      if (
        controlledValue !== undefined &&
        (controlledValue === item || (controlledValue == null && item == null))
      ) {
        return;
      }
      onSelectItem(item);
    },
    [onSelectItem, controlledValue]
  );

  const cleanedDropDownPickerProps = dropDownPickerProps
    ? omit(dropDownPickerProps, DropDownPickerSpecificPropsList)
    : {};

  const filteredItems = useMemo(() => {
    if (!searchable || !searchText) return items;
    const lower = searchText.toLowerCase();
    return items.filter((item) => item.label?.toLowerCase().includes(lower));
  }, [searchable, searchText, items]);

  return (
    <View
      style={{
        width: '100%',
        zIndex: open ? 1000 : 0,
        elevation: open ? 10 : 0,
        position: 'relative',
        overflow: 'visible' as const,
      }}
    >
      {searchable && open && (
        <View style={styles.searchContainer}>
          <SearchInput
            value={searchText}
            onChangeText={setSearchText}
            placeholder={searchPlaceholder ?? 'Search...'}
            autoFocus
            height={36}
            width={'100%'}
            backgroundColor={Colors.role.surface}
          />
        </View>
      )}
      <DropDownPicker
        testID="dropdown-picker"
        // react-native-dropdown-picker defaults to its LIGHT theme, whose arrow,
        // tick and close glyphs are black PNG *assets*. Style overrides cannot
        // reach an image, so the mode has to be handed to the library itself —
        // its DARK theme ships white versions of the same three icons.
        theme={themeMode === 'dark' ? 'DARK' : 'LIGHT'}
        placeholder={label}
        open={open}
        setOpen={(o) => {
          setOpen(o);
          setSearchText('');
        }}
        onChangeValue={(selectedValue) => {
          if (controlledValue === undefined) {
            setInternalValue(selectedValue as T);
          }
          notifySelect(selectedValue as T | null);
        }}
        items={filteredItems}
        itemKey="value"
        value={value}
        disabled={disabled}
        setValue={(callback) => {
          const newValue = callback(value);
          if (controlledValue === undefined) {
            setInternalValue(newValue);
          }
          notifySelect(newValue as T | null);
        }}
        style={disabled ? styles.disabled : dropDownPickerProps?.style ?? {}}
        selectedItemContainerStyle={
          selectedItemContainerStyle ?? {
            backgroundColor: Colors.primary,
          }
        }
        selectedItemLabelStyle={
          selectedItemLabelStyle ?? {
            color: Colors.role.textOnBrand,
          }
        }
        TickIconComponent={showTickIcon ? TickIconComponent : undefined}
        showTickIcon={showTickIcon}
        listMode={props.listMode}
        scrollViewProps={{
          nestedScrollEnabled: true,
        }}
        dropDownContainerStyle={props.dropDownContainerStyle}
        containerProps={{
          style: {
            zIndex: open ? 10 : 1,
          },
        }}
        props={{ activeOpacity: 0.75 }}
        {...cleanedDropDownPickerProps}
      />
      {errorMessage && <HelperText type="error">{errorMessage}</HelperText>}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  searchContainer: {
    marginBottom: 8,
  },
  dropdown: {
    backgroundColor: role.surface,
    textAlign: 'right',
  },
  disabled: {
    backgroundColor: role.fillDisabled,
  },
}));
